import { supabase } from '../lib/supabase';
import type { User, UserRole } from '../types/auth';
import { userConfirmationService } from './supabase/user-confirmation';
import { userEmailService } from './supabase/user-email';

export const userService = {
  getAllUsers: async (): Promise<User[]> => {
    // Get all user profiles
    const { data: profiles, error: profilesError } = await supabase
      .from('user_profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (profilesError) throw profilesError;

    // Map profiles to users, including emails if available
    const users = profiles.map(profile => ({
      id: profile.id,
      firstName: profile.first_name,
      lastName: profile.last_name,
      email: profile.email || '', // Use email from profile if available
      role: profile.role,
      isActive: profile.is_active
    }));

    return users;
  },

  createUser: async (userData: Omit<User, 'id'> & { password: string }): Promise<User> => {
    // Check if current user is a system_admin
    const { data: currentUser } = await supabase.auth.getUser();
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('role')
      .eq('id', currentUser.user?.id)
      .single();
      
    if (!profile || profile.role !== 'system_admin') {
      throw new Error('User not allowed to create new users');
    }
    
    try {
      // First, check if a user with this email already exists but is inactive
      const { data: existingProfiles } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('email', userData.email);
      
      // If we found an inactive user with this email, reactivate them
      if (existingProfiles && existingProfiles.length > 0) {
        const existingProfile = existingProfiles[0];
        
        if (!existingProfile.is_active) {
          console.log('Found inactive user with this email. Reactivating...');
          
          // Update the user profile
          const { data: updatedProfile, error: updateError } = await supabase
            .from('user_profiles')
            .update({
              first_name: userData.firstName,
              last_name: userData.lastName,
              role: userData.role,
              is_active: true,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingProfile.id)
            .select()
            .single();
          
          if (updateError) {
            console.error('Error reactivating user:', updateError);
            throw updateError;
          }
          
          // Try to reset the user's password
          try {
            await supabase.auth.resetPasswordForEmail(userData.email, {
              redirectTo: `${window.location.origin}/reset-password`
            });
            console.log('Password reset email sent to reactivated user.');
          } catch (resetError) {
            console.warn('Could not send password reset email:', resetError);
          }
          
          return {
            id: updatedProfile.id,
            firstName: updatedProfile.first_name,
            lastName: updatedProfile.last_name,
            email: userData.email,
            role: updatedProfile.role,
            isActive: updatedProfile.is_active
          };
        } else {
          // User exists and is active
          throw new Error('A user with this email already exists and is active. Please use a different email address.');
        }
      }
      
      // Create the user with the actual password directly
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          data: {
            first_name: userData.firstName,
            last_name: userData.lastName,
            role: userData.role
          }
        }
      });

      if (authError) {
        // Check if the error is due to a duplicate email
        if (authError.message.includes('email') && authError.message.includes('already')) {
          throw new Error('A user with this email already exists in the authentication system. Please use a different email address or contact the system administrator.');
        }
        throw authError;
      }
      
      if (!authData.user) {
        throw new Error('Failed to create user');
      }
      
      // Try to confirm the user's email using our Edge Function
      try {
        await userConfirmationService.confirmUser(authData.user.id);
        console.log('User confirmed successfully. They can now log in without email verification.');
      } catch (confirmError) {
        console.warn('Could not auto-confirm user. They may need to verify their email:', confirmError);
        // Continue anyway, we'll still create the profile
      }

      // Create the user profile with the email
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .insert([{
          id: authData.user.id,
          first_name: userData.firstName,
          last_name: userData.lastName,
          email: userData.email, // Store email in profile for easier access
          role: userData.role,
          is_active: true
        }])
        .select()
        .single();

      if (profileError) {
        console.error('Error creating user profile:', profileError);
        throw profileError;
      }

      return {
        id: profileData.id,
        firstName: profileData.first_name,
        lastName: profileData.last_name,
        email: userData.email,
        role: profileData.role,
        isActive: profileData.is_active
      };
    } catch (error) {
      // Improve error handling with more specific messages
      if (error instanceof Error) {
        if (error.message.includes('already registered') || error.message.includes('already exists')) {
          throw new Error('A user with this email already exists. Please use a different email address.');
        }
      }
      console.error('Error in createUser:', error);
      throw error;
    }
  },

  updateUser: async (userData: Partial<User> & { id: string }): Promise<User> => {
    // Check if current user is a system_admin
    const { data: currentUser } = await supabase.auth.getUser();
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('role')
      .eq('id', currentUser.user?.id)
      .single();
      
    if (!profile || profile.role !== 'system_admin') {
      throw new Error('User not allowed to update users');
    }
    
    // Update the user profile
    const updateData: any = {
      first_name: userData.firstName,
      last_name: userData.lastName,
      role: userData.role,
      is_active: userData.isActive,
      updated_at: new Date().toISOString()
    };
    
    // Only update email if it's provided and not empty
    if (userData.email) {
      updateData.email = userData.email;
    }
    
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updateData)
      .eq('id', userData.id)
      .select()
      .single();

    if (error) throw error;
    
    // Get the email from the updated profile or use the provided email
    const email = data.email || userData.email || '';
    
    // If the email was updated, also update it in our email service
    if (userData.email && userData.email !== email) {
      try {
        await userEmailService.updateUserEmail(userData.id, userData.email);
      } catch (emailError) {
        console.warn('Could not update email in email service:', emailError);
      }
    }
    
    return {
      id: data.id,
      firstName: data.first_name,
      lastName: data.last_name,
      email: email,
      role: data.role,
      isActive: data.is_active
    };
  },

  deleteUser: async (userId: string): Promise<void> => {
    // Check if current user is a system_admin
    const { data: currentUser } = await supabase.auth.getUser();
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('role')
      .eq('id', currentUser.user?.id)
      .single();
      
    if (!profile || profile.role !== 'system_admin') {
      throw new Error('User not allowed to delete users');
    }
    
    try {
      // Get the user profile
      const { data: userData } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (!userData) {
        throw new Error('User not found');
      }
      
      // Set user as inactive in our database
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (updateError) throw updateError;
      
      // Note: We can't delete users from the auth system or update their email from the client side
      // This would require server-side functions with admin privileges
      // For now, we'll just mark them as inactive in our database
      console.log('User marked as inactive. Full deletion requires server-side admin functions.');
    } catch (error) {
      console.error('Error in deleteUser:', error);
      throw error;
    }
  },

  resetPassword: async ({ userId, newPassword, email }: { userId: string, newPassword: string, email: string }): Promise<void> => {
    // Check if current user is a system_admin
    const { data: currentUser } = await supabase.auth.getUser();
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('role')
      .eq('id', currentUser.user?.id)
      .single();
      
    if (!profile || profile.role !== 'system_admin') {
      throw new Error('User not allowed to reset passwords');
    }
    
    // We need the email to send a password reset
    if (!email) {
      throw new Error('Email is required for password reset');
    }
    
    // Send a password reset email to the user
    const { error } = await supabase.auth.resetPasswordForEmail(
      email,
      {
        redirectTo: `${window.location.origin}/reset-password`
      }
    );

    if (error) throw error;
  }
};
